import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback } from 'react';

import { getLoginRequest, getTokenRequest } from './msalConfig';

/** Shape returned by {@link useAuth}. */
export interface UseAuth {
  /** True once an account is present in the MSAL cache. */
  isAuthenticated: boolean;
  /** The active account, or `null` when signed out. */
  account: AccountInfo | null;
  /** Display name for the signed-in user (falls back to username). */
  displayName: string | null;
  /** Begin interactive sign-in (redirect flow). */
  login: () => Promise<void>;
  /** Sign out (redirect flow). */
  logout: () => Promise<void>;
  /**
   * Acquire an access token for the WorkLogix API scope.
   * Tries silent acquisition first; on an interaction-required error it kicks
   * off a redirect (which navigates away) rather than looping silently.
   */
  acquireToken: () => Promise<string>;
}

/**
 * MSAL's error code for "an interaction is already in flight". It is raised from a
 * flag MSAL persists in the cache, NOT from anything actually happening — so if a
 * redirect never completes, the flag is left set and every later sign-in attempt
 * fails on a stale bit with no way for the user to clear it.
 */
const INTERACTION_IN_PROGRESS = 'interaction_in_progress';

/** True for the stuck-interaction error specifically, not auth errors generally. */
function isInteractionInProgress(error: unknown): boolean {
  return error instanceof BrowserAuthError && error.errorCode === INTERACTION_IN_PROGRESS;
}

/**
 * Drop MSAL's persisted interaction-status flag.
 *
 * There is no public API for this, so the key is removed directly — deliberately
 * NARROW: only keys containing `interaction.status`, never the account or token
 * cache, so recovering does not sign anyone out or discard a usable token. Both
 * stores are swept because the cache location is configurable and a redirect that
 * half-completed can leave the flag in either.
 *
 * Clearing a genuinely in-flight interaction is harmless: during a real redirect the
 * page is navigating away and nobody is clicking. The flag being set while the user
 * is still sitting on the sign-in button is precisely the broken state.
 */
function clearStuckInteraction(): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null && key.includes('interaction.status')) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      store.removeItem(key);
    }
  }
}

/**
 * Run a redirect-initiating MSAL call, recovering ONCE from a stale
 * interaction-status flag.
 *
 * WHY THIS EXISTS: on 2026-08-24 a bad build shipped a client id that did not
 * exist, so every sign-in redirect failed mid-flight and left this flag set. Even
 * after the config was fixed, affected browsers still could not sign in — the only
 * cure was clearing site data by hand, which no ordinary user will discover. The
 * same trap is reachable in normal use by closing the tab mid-redirect.
 *
 * Retries EXACTLY once. A second failure propagates rather than looping, because a
 * sign-in that silently retries forever is worse than one that reports an error.
 */
async function withInteractionRecovery(run: () => Promise<void>): Promise<void> {
  try {
    await run();
  } catch (error) {
    if (!isInteractionInProgress(error)) {
      throw error;
    }
    clearStuckInteraction();
    await run();
  }
}

/**
 * Thin, typed wrapper over the MSAL React hooks that centralizes login/logout
 * and token acquisition for the WorkLogix API scope.
 */
export function useAuth(): UseAuth {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account = accounts[0] ?? null;

  const login = useCallback(async (): Promise<void> => {
    await withInteractionRecovery(() => instance.loginRedirect(getLoginRequest()));
  }, [instance]);

  const logout = useCallback(async (): Promise<void> => {
    await instance.logoutRedirect();
  }, [instance]);

  const acquireToken = useCallback(async (): Promise<string> => {
    const active = instance.getActiveAccount() ?? accounts[0];
    if (active === undefined) {
      // No account at all — send the user through interactive sign-in.
      await withInteractionRecovery(() => instance.acquireTokenRedirect(getLoginRequest()));
      // acquireTokenRedirect navigates away; this line is not reached.
      throw new Error('Redirecting to sign-in.');
    }
    try {
      const result = await instance.acquireTokenSilent({ ...getTokenRequest(), account: active });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await withInteractionRecovery(() =>
          instance.acquireTokenRedirect({ ...getTokenRequest(), account: active })
        );
        throw new Error('Redirecting to re-authenticate.');
      }
      throw error;
    }
  }, [instance, accounts]);

  const displayName = account?.name ?? account?.username ?? null;

  return { isAuthenticated, account, displayName, login, logout, acquireToken };
}
